import { Link } from 'react-router-dom';
import { isMichromaBrand } from '@/constants/brandPattern';
import { useBrandPattern } from '@/hooks/useBrandPattern';
import { SECTION_LABEL_FONT_SIZE } from '@/components/base/SectionHeading';

const navItems = [
  { label: 'home', href: '/' },
  { label: 'company', href: '/company' },
  { label: 'works', href: '/works' },
  { label: 'recruit', href: '/recruit' },
  { label: 'contact', href: '/contact' },
  { label: 'map', href: '/map' },
];

export default function Footer() {
  const { pattern } = useBrandPattern();
  const michroma = isMichromaBrand(pattern);

  return (
    <footer style={{ background: '#F2F2F0' }} className="w-full">
      <div className="mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16">
          {/* Brand */}
          <div className="cadec-inline-cq">
            <div className="mb-2">
              <span
                className={`cadec-footer-wordmark font-normal tracking-tight ${
                  michroma ? 'cadec-footer-wordmark--michroma' : ''
                }`}
                style={{
                  fontFamily: pattern.fontFamily,
                  fontWeight: pattern.wordmarkWeight,
                  letterSpacing: pattern.wordmarkLetterSpacing,
                  color: '#111111',
                }}
              >
                {pattern.wordmarkChars.join('')}
              </span>
            </div>
            <p
              className="mb-1.5"
              style={{ color: '#3F3F3A', fontFamily: '"Noto Sans JP", sans-serif', letterSpacing: '0.05em', fontSize: '16px', fontWeight: 500 }}
            >
              株式会社キャデック
            </p>
            <p
              className={`cadec-footer-company-en mb-7 text-balance ${
                michroma ? 'cadec-footer-company-en--michroma' : ''
              }`}
              style={{
                color: '#52525B',
                fontFamily: pattern.fontFamily,
                fontWeight: 400,
                letterSpacing: '0.04em',
              }}
            >
              Computer Added Development &amp; Engineering Co.,ltd.
            </p>
            <div className="space-y-2">
              <p
                className="[word-break:keep-all]"
                style={{ color: '#404040', fontFamily: '"Noto Sans JP", sans-serif', lineHeight: '1.8', fontSize: '15px' }}
              >
                青森県並びに八戸市誘致企業
              </p>
              <p
                className="[word-break:keep-all]"
                style={{ color: '#404040', fontFamily: '"Noto Sans JP", sans-serif', lineHeight: '1.8', fontSize: '15px' }}
              >
                一級建築士事務所
              </p>
            </div>
          </div>

          {/* Nav */}
          <div>
            <div
              className="mb-3 font-medium tracking-widest"
              style={{ color: '#1F2937', fontFamily: '"Inter", sans-serif', fontSize: SECTION_LABEL_FONT_SIZE }}
            >
              NAVIGATION
            </div>
            <div className="mb-5 h-px w-8" style={{ background: '#9CA3AF' }} />
            <nav className="flex flex-col gap-3.5">
              {navItems.map((item) => (
                <Link
                  key={item.label}
                  to={item.href}
                  className="group inline-flex w-fit max-w-full items-center gap-0 cursor-pointer tracking-widest whitespace-nowrap transition-all duration-300 ease-out text-[#5f6977] hover:gap-2.5 hover:text-[#0a0a0a] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/80 focus-visible:ring-offset-2 focus-visible:ring-offset-[#F2F2F0]"
                  style={{ fontFamily: '"Inter", sans-serif', fontSize: '15px', fontWeight: 500 }}
                >
                  <span
                    className="inline-block h-0.5 w-0 shrink-0 rounded-full bg-[#B48C50] transition-all duration-300 ease-out group-hover:w-4"
                    aria-hidden
                  />
                  <span className="transition-transform duration-300 ease-out group-hover:translate-x-0.5">
                    {item.label}
                  </span>
                </Link>
              ))}
            </nav>
          </div>

          {/* Contact info */}
          <div>
            <div
              className="mb-3 font-medium tracking-widest"
              style={{ color: '#1F2937', fontFamily: '"Inter", sans-serif', fontSize: SECTION_LABEL_FONT_SIZE }}
            >
              CONTACT
            </div>
            <div className="mb-5 h-px w-8" style={{ background: '#9CA3AF' }} />
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <i className="ri-map-pin-line mt-0.5" style={{ fontSize: '20px', color: '#4B5563', flexShrink: 0 }} />
                <span
                  className="leading-relaxed"
                  style={{ color: '#374151', fontFamily: '"Noto Sans JP", sans-serif', lineHeight: '1.9', fontSize: '15px' }}
                >
                  〒039-1114 青森県八戸市北白山台5丁目2-5<br />東奥日報社八戸ビル5階
                </span>
              </div>
              <div className="flex items-center gap-3">
                <i className="ri-phone-line" style={{ fontSize: '20px', color: '#4B5563', flexShrink: 0 }} />
                <span style={{ color: '#374151', fontFamily: '"Inter", sans-serif', fontSize: '16px', fontWeight: 500 }}>0178-51-8296</span>
              </div>
              <div className="flex items-center gap-3">
                <i
                  className="fa-solid fa-fax flex h-5 w-5 shrink-0 items-center justify-center"
                  style={{ fontSize: '16px', color: '#4B5563' }}
                  aria-hidden
                />
                <span style={{ color: '#374151', fontFamily: '"Inter", sans-serif', fontSize: '16px', fontWeight: 500 }}>
                  0178-51-8297
                </span>
              </div>
              <div className="group/mailrow flex max-w-full items-center gap-3">
                <i
                  className="ri-mail-line flex-shrink-0 text-[#4B5563] transition-colors duration-300 group-hover/mailrow:text-[#B48C50]"
                  style={{ fontSize: '20px' }}
                />
                <Link
                  to="/contact"
                  className="inline-flex w-fit min-w-0 max-w-full items-center gap-0 text-[#5f6977] transition-all duration-300 ease-out group-hover/mailrow:gap-2.5 group-hover/mailrow:text-[#0a0a0a] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/80 focus-visible:ring-offset-2 focus-visible:ring-offset-[#F2F2F0]"
                  style={{ fontFamily: '"Inter", sans-serif', fontSize: '16px' }}
                  aria-label="お問い合わせフォームへ"
                >
                  <span
                    className="inline-block h-0.5 w-0 shrink-0 rounded-full bg-[#B48C50] transition-all duration-300 ease-out group-hover/mailrow:w-4"
                    aria-hidden
                  />
                  <span className="min-w-0 break-all transition-transform duration-300 ease-out group-hover/mailrow:translate-x-0.5">
                    cadec@cadec.co.jp
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div
          className="mt-14 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4"
          style={{ borderTop: '1px solid #D4D4CE' }}
        >
          <p style={{ color: '#5C5C56', fontFamily: '"Inter", sans-serif', fontSize: '14px' }}>
            © 2025 株式会社キャデック All Rights Reserved.
          </p>
          <p style={{ color: '#4B4B45', fontFamily: '"Noto Sans JP", sans-serif', fontSize: '14px' }}>
            一級建築士事務所 青森県知事登録 第844号
          </p>
        </div>
      </div>
    </footer>
  );
}
